import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash';
import MainTextField from '../../MUI/MainTextField';
import MainCheckbox from '../../MUI/MainCheckbox/MainCheckbox';
import OncoCareRisks from './OncoCareRisks';
import TitleTypography from '../../MUI/TitleTypography';
import PaymentFrequencyTypes from '../../FormComponents/PaymentFrequencyTypes';
import useDeviceResize from '../../../hooks/useDeviceResize';
import Validation from '../../Validation/Validation';
import { TRAVEL_MAX_AGE, TRAVEL_MIN_AGE } from '../../../constants/travelCalcConstants';
import { updateCalculationFormAge } from '../../../helpers/age';

import useStyles from './makeStyles';

const Form = ({
  age,
  setAge,
  showAgeInput,
  formProps,
  setWithChildren,
  withChildren,
  showWithChildrenCheckbox,
  riskAmount,
  setRiskAmount,
  riskAmounts,
  paymentFrequencies,
  prices,
  setPaymentFrequency,
  paymentFrequency,
}) => {
  const { errors, setValue } = formProps;

  const { t } = useTranslation();
  const classes = useStyles();
  const isDeviceMobileSize = useDeviceResize('sm');

  const updateAge = useCallback(
    (e) => {
      updateCalculationFormAge(e.target.value, setValue, setAge);
    },
    [setAge, setValue],
  );

  return (
    <Grid container spacing={4}>
      {showAgeInput && (
        <Grid item xs={12} sm={4}>
          <Validation errors={errors} name="age">
            <MainTextField
              label="personalInfo.age"
              value={age || ''}
              variant="outlined"
              type="number"
              size="small"
              fullWidth
              name="age"
              step={1}
              onChange={updateAge}
              InputProps={{
                inputProps: {
                  max: TRAVEL_MAX_AGE,
                  min: TRAVEL_MIN_AGE,
                },
              }}
              required
            />
          </Validation>
        </Grid>
      )}
      {
        showWithChildrenCheckbox && (
          <Grid item xs={12} sm={12}>
            <div className={classes.withChildrenBlock}>
              <label>
                <MainCheckbox
                  checked={withChildren}
                  onChange={(event) => setWithChildren(event.target.checked)}
                  color="primary"
                  style={{ marginRight: '1rem' }}
                />
                {t('oncoCareCalculator.withChildren')}
              </label>
            </div>
          </Grid>
        )
      }
      {
        !!riskAmounts.length && (
          <Grid item xs={12} className={classes.sectionBox}>
            <div className={classes.title}>
              <TitleTypography component="div" className={classes.sectionTitle}>
                {t('oncoCareCalculator.riskAmount')}
              </TitleTypography>
              {isDeviceMobileSize && (
                <Typography className={classes.mobileTitle}>
                  {`֏ ${parseInt(riskAmount[0], 10) / 1000000} ${t('oncoCareCalculator.million')}`}
                </Typography>
              )}
            </div>
            <Grid item className={classes.sectionRow}>
              <OncoCareRisks
                riskAmount={riskAmount}
                setRiskAmount={setRiskAmount}
                riskAmounts={riskAmounts}
              />
            </Grid>
          </Grid>
        )
      }
      {
        isEmpty(paymentFrequencies) || isEmpty(prices) ? null
          : (
            <Grid item xs={12} className={classes.sectionBox}>
              <TitleTypography component="div" className={classes.sectionTitle}>
                {t('oncoCareCalculator.paymentFrequencyType')}
              </TitleTypography>
              <Grid container item className={classes.paymentFrequenciesWrapper} spacing={1}>
                <PaymentFrequencyTypes
                  prices={prices}
                  paymentFrequencies={paymentFrequencies}
                  setPaymentFrequency={setPaymentFrequency}
                  paymentFrequency={paymentFrequency}
                />
              </Grid>
            </Grid>
          )
      }
    </Grid>
  );
};

Form.propTypes = {
  age: PropTypes.number.isRequired,
  setAge: PropTypes.func.isRequired,
  showAgeInput: PropTypes.bool.isRequired,
  formProps: PropTypes.shape({
    errors: PropTypes.shape({}),
    setValue: PropTypes.func,
  }).isRequired,
  setWithChildren: PropTypes.func.isRequired,
  withChildren: PropTypes.bool.isRequired,
  showWithChildrenCheckbox: PropTypes.bool.isRequired,
  riskAmount: PropTypes.arrayOf(PropTypes.number).isRequired,
  setRiskAmount: PropTypes.func.isRequired,
  riskAmounts: PropTypes.arrayOf(PropTypes.string).isRequired,
  paymentFrequencies: PropTypes.shape({}).isRequired,
  prices: PropTypes.shape({}).isRequired,
  setPaymentFrequency: PropTypes.func.isRequired,
  paymentFrequency: PropTypes.shape({}).isRequired,
};

export default Form;
